/**********************************************************************************************************
 *   BASE IMPORTS
 **********************************************************************************************************/
import { useEffect, useState } from 'react'
import styled from 'styled-components'

/**********************************************************************************************************
 *   SHARED IMPORTS
 **********************************************************************************************************/
import { useDomainsQuery, useProductQuery } from 'api/shop'
import { OrderType } from 'models/enums'
import { CustomFieldGroup, PendingItem } from 'models/shop'
import { removeFromItemsByNameID, setCustomFieldsOpen } from 'store/slices/shopSlice'
import { getDomainName } from 'helpers/utils'
import { useAppDispatch, useAppSelector } from 'store/hooks'

import Search from 'containers/shop/domains/search'
import SearchResult from 'containers/shop/domains/searchResult'
import CustomFieldsLightbox from 'containers/shop/domains/lightbox/customFieldsLightbox'
import ContactDetailsLightbox from 'containers/shop/domains/lightbox/contactDetailsLightbox'

/**********************************************************************************************************
 *   COMPONENT START
 **********************************************************************************************************/
export default function Register() {
    const dispatch = useAppDispatch()
    const { product, searchTerm, customFieldsOpen, items } = useAppSelector((state) => state.shop)
    const [openLightbox, setOpenLightbox] = useState(false)
    const { data: productData, isSuccess: isProductSuccess } = useProductQuery(product, { skip: !product })
    const hasDomainContact = items.filter(({ product_type }) => {
        return product_type === 'domain'
    }).every((item: PendingItem) => {
        return item.domain_contact_fields
    })
    useDomainsQuery(searchTerm, { skip: !searchTerm })

    useEffect(() => {
        if (isProductSuccess && productData?.service_type === 'domain' && !hasDomainContact) {
            setOpenLightbox(true)
        }
    }, [hasDomainContact, isProductSuccess, productData])

    useEffect(() => {
        if (!openLightbox && isProductSuccess && productData?.service_type === 'domain') {
            const timer = setTimeout(() => {
                const itemName = `${getDomainName(searchTerm || '')}${productData.name}`
                const domain = items.find(({ name, product_type }: PendingItem) => {
                    return product_type === 'domain' && name === itemName
                })

                if (domain && !domain?.domain_contact_fields) {
                    dispatch(removeFromItemsByNameID({ product_id: productData.id, name: itemName }))
                }
            }, 500)

            return () => clearTimeout(timer)
        }
    }, [openLightbox, isProductSuccess])

    /*   RENDER COMPONENT
    **********************************************************************************************************/
    return (
        <RegisterWrapper>
            <Search type={OrderType.Register}/>
            <SearchResult/>
            {hasDomainContact &&
                productData?.custom_field_groups.map((customFieldGroup: CustomFieldGroup, index: number) => (
                    <CustomFieldsLightbox
                        status={customFieldsOpen}
                        close={() => dispatch(setCustomFieldsOpen(false))}
                        key={index}
                        productID={productData.id}
                        customFieldGroup={customFieldGroup}
                    />
                ))
            }
            {productData &&
                <ContactDetailsLightbox domain={productData} status={openLightbox} close={() => setOpenLightbox(false)}/>
            }
        </RegisterWrapper>
    )
}

/**********************************************************************************************************
 *   CUSTOM STYLING
 **********************************************************************************************************/
const RegisterWrapper = styled.main`
    width: 100%;
`
